import { z } from "zod";

/**
 * Create Account form validation (#1545).
 *
 * Scope is intentionally narrow: RHF/Zod owns the two validated user-facing
 * fields on the phone/OTP path — the phone number and the SMS code. Country
 * detection, live phone formatting, Google/Apple OAuth, the test sign-in path,
 * and Stytch loginOrCreate/authenticate all remain in the screen; the schema
 * only decides when those fields are valid enough to submit.
 *
 * `phoneDigits` holds the raw national-significant digits (no country code, no
 * formatting punctuation). The per-country maximum length is enforced at the
 * input as the user types; the schema owns the lower bound so the "Continue"
 * button and error copy have a single source of truth.
 */

const PHONE_MIN_DIGITS = 7;
const OTP_LENGTH = 6;

export const createAccountPhoneSchema = z.object({
  phoneDigits: z
    .string()
    .regex(/^\d*$/, "phone_digits_only")
    .min(PHONE_MIN_DIGITS, "phone_too_short"),
});

export const createAccountOtpSchema = z.object({
  code: z
    .string()
    .regex(/^\d*$/, "otp_digits_only")
    .length(OTP_LENGTH, "otp_length"),
});

// The default export models the primary (phone) form so callers can address it
// as "the createAccount schema" per #1545.
export const createAccountSchema = createAccountPhoneSchema;

export type CreateAccountPhoneValues = z.infer<typeof createAccountPhoneSchema>;
export type CreateAccountOtpValues = z.infer<typeof createAccountOtpSchema>;
export type CreateAccountValues = CreateAccountPhoneValues;

export { PHONE_MIN_DIGITS, OTP_LENGTH };
